import {
    CUSTOM_MISCONDUCT_CODE_ID,
    ICardCodeMap,
    MatchPhone,
    MisconductCodes,
    Reason,
} from "refsix-js-models";
import { store } from "../redux/store";
import { getCustomCodes } from "../utils/Misconduct";
import { copyObject, getPreSetCodes, findPresetCodeByName } from "refsix-core";

export const ALL_CODES = "allcodes";

export enum CardType {
    Yellow = "Yellow",
    Red = "Red",
}

export interface MisconductCodeTotal {
    id: string;
    code: string;
    value: number;
}

/**
 * Transforms `ICardCodeMap` into `MisconductCodeTotal[]`, sorted by highest values,
 * for easier processing
 */
export function transformToMisconductCodeTotals(
    codeMap: ICardCodeMap
): MisconductCodeTotal[] {
    return Object.keys(codeMap)
        .map((keyCode) => ({
            id: keyCode.split("-")[0],
            code: keyCode.split("-")[1],
            value: codeMap[keyCode],
        }))
        .sort((a, b) => b.value - a.value);
}

/**
 * Filters codes based on passed in ID.
 */
export function getAllCodesForId(
    id: string,
    codes: MisconductCodeTotal[]
): MisconductCodeTotal[] {
    return id === ALL_CODES ? codes : codes.filter((code) => code.id === id);
}

export function getMisconductCodes(): MisconductCodes[] {
    let customCodes = store.getState().settings.settings.customCodes[0];
    return [
        customCodes || getCustomCodes(),
        ...getPreSetCodes().sort((a, b) =>
            a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        ),
    ];
}

export function findCodeByCountryId(id: string): MisconductCodes | undefined {
    return getMisconductCodes().find((code) => code.id === id);
}

export function findCodeByName(name: string): MisconductCodes | undefined {
    // The user's custom codes take precedence (they aren't known to core), then
    // fall back to the shared preset name lookup in core.
    const customCodes =
        store.getState().settings.settings.customCodes[0] || getCustomCodes();
    if (
        name &&
        customCodes &&
        customCodes.name &&
        customCodes.name.toLowerCase() === name.toLowerCase()
    ) {
        return customCodes;
    }
    return findPresetCodeByName(name);
}

/**
 * Embeds a snapshot of the user's custom misconduct codes into the match.
 *
 * Custom codes change over time, so when a match starts using them we freeze the
 * codes that were in effect at that moment onto the match itself. This mirrors the
 * watch flow (`WatchMatchHelper.buildMatchForWatch`), which embeds
 * `customMisconductCodes` when a match is sent to the watch to be played.
 *
 * No-op for non-custom matches or when codes are already embedded.
 */
export function embedCustomMisconductCodes(match: MatchPhone): MatchPhone {
    if (
        match.misconductCodeId === CUSTOM_MISCONDUCT_CODE_ID &&
        !match.customMisconductCodes?.length
    ) {
        const customCodes = findCodeByCountryId(
            CUSTOM_MISCONDUCT_CODE_ID
        )?.codes;
        // Deep copy so the embedded snapshot doesn't alias the live Redux settings
        // array (codes carry nested subCodes); a later settings edit must not mutate it.
        match.customMisconductCodes = customCodes ? copyObject(customCodes) : [];
    }
    return match;
}

/**
 * Resolves the misconduct reason list for a match (e.g. for the Add Event reason
 * dropdown).
 *
 * For custom codes it prefers the codes embedded in the match (the historical
 * snapshot taken when the match became a result), falling back to the user's
 * current settings only for legacy/edge matches that lack embedded codes.
 */
export function getMisconductCodesForMatch(match: MatchPhone): Reason[] {
    if (match.misconductCodeId === CUSTOM_MISCONDUCT_CODE_ID) {
        return match.customMisconductCodes?.length
            ? match.customMisconductCodes
            : findCodeByCountryId(CUSTOM_MISCONDUCT_CODE_ID)?.codes || [];
    }
    if (match.misconductCodeId) {
        return findCodeByCountryId(match.misconductCodeId)?.codes || [];
    }
    return [];
}
